import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2 } from "lucide-react";
import api, { API_BASE } from "@/lib/api";

// pdfjs-dist v5 ships an ES-module worker ONLY. CRA/webpack builds the app as a
// classic script, so pdf.js would create a *classic* Worker for the .mjs file →
// "Cannot use import statement outside a module" → every PDF fails to render in
// the production build (dev server happens to paper over it). Worse, letting
// webpack process the worker via `new URL(..., import.meta.url)` rewrites its
// imports to absolute build-machine paths that 404 on the server.
//
// Fix: serve the pristine, self-contained worker from /public (copied on build
// by the `sync-pdf-worker` script, version-matched to the installed pdfjs) and
// instantiate it OURSELVES as a MODULE worker, handing it to pdf.js via
// `workerPort`. This bypasses pdf.js's broken classic-worker creation entirely.
if (typeof window !== "undefined" && !pdfjs.GlobalWorkerOptions.workerPort) {
  const workerUrl = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;
  pdfjs.GlobalWorkerOptions.workerPort = new Worker(workerUrl, { type: "module" });
}

// View-only PDF viewer. Renders pages to <canvas> via PDF.js so the raw PDF
// file is NEVER exposed as a downloadable link, and there is no browser PDF
// toolbar (no download / print / open buttons). Text + annotation layers are
// disabled so the content can't be selected/copied either.
//
// The PDF is fetched with the app's Bearer token (same as every other API
// call) instead of relying on the auth cookie — this is what makes it work in
// DevAdmin "Simulate role" mode (where only the localStorage token is set, not
// the cookie) and in any cross-domain / SameSite cookie situation.
export function PdfViewer({ url }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);
  const [err, setErr] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fetch the PDF with the Bearer token, then render from an in-memory blob URL.
  useEffect(() => {
    if (!url) return;
    let alive = true;
    let objectUrl = null;
    setErr(false);
    setNumPages(0);
    setBlobUrl(null);
    (async () => {
      try {
        // 1) Get a short-lived signed token via axios (cookie/Bearer auth — the
        //    same mechanism the rest of the app uses, works cross-origin on prod).
        const rel = url.startsWith(API_BASE) ? url.slice(API_BASE.length) : url;
        const tokenPath = rel.replace(/\/pdf(\?.*)?$/, "/pdf-token");
        const { data } = await api.get(tokenPath);
        // 2) Stream the PDF with the token in the query string — no auth header,
        //    so it's a simple request (no CORS preflight), identical to the video.
        const streamUrl = `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(data.token)}`;
        const resp = await fetch(streamUrl);
        if (!resp.ok) throw new Error(`pdf ${resp.status}`);
        const blob = await resp.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  const file = useMemo(() => (blobUrl ? { url: blobUrl } : null), [blobUrl]);
  const options = useMemo(() => ({ disableStream: true, disableRange: true }), []);

  const blockDownload = (e) => e.preventDefault();

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-y-auto rounded-xl bg-slate-200/70 dark:bg-slate-950 select-none"
      onContextMenu={blockDownload}
      onDragStart={blockDownload}
      data-testid="pdf-viewer"
    >
      {err ? (
        <div className="p-6 text-sm text-slate-500">Could not load the lesson PDF.</div>
      ) : !file ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading lesson…</div>
      ) : (
        <Document
          file={file}
          options={options}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={() => setErr(true)}
          loading={<div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading lesson…</div>}
          error={<div className="p-6 text-sm text-slate-500">Could not load the lesson PDF.</div>}
        >
          {width > 0 && Array.from({ length: numPages }, (_, i) => (
            <div key={i} className="flex justify-center py-2 px-2">
              <Page
                pageNumber={i + 1}
                width={Math.min(width - 16, 900)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-sm rounded-md overflow-hidden bg-white"
              />
            </div>
          ))}
        </Document>
      )}
    </div>
  );
}
